<template>
    <div class="dashboard-v3">
        <!-- Background gradient -->

        <!-- Main Content -->
        <div class="dashboard-content">
            <!-- Header -->
            <div class="dashboard-header">
                <div class="greeting-section">
                    <h1 class="greeting-text">
                        {{ $t('dashboard.greeting.' + timeOfDayGreeting) }}
                        <span class="user-name">{{ capitalizedUserName }}</span> 👋
                    </h1>
                </div>

                <div class="header-actions">
                    <!-- <DateQuickFilter v-model="selectedPeriod" @update:modelValue="onPeriodChange" /> -->
                    <div class="view-toggles">
                        <button class="toggle" @click="goToStatistics">{{ $t('dashboard.tables.vue_stats') }}</button>
                        <button class="toggle active">{{ $t('dashboard.tables.vue_ops') }}</button>
                    </div>
                </div>
            </div>

            <!-- KPI Cards Row -->
            <div class="kpi-row">
                <DashboardKpiCard
                    :label="$t('dashboard.kpis.upcomingReservations')"
                    :value="stats.upcomingReservations"
                    :variation="stats.upcomingVariation"
                    :show-weather="true"
                    :weather="currentWeather"
                >
                    <template #icon>
                        <CalendarDays :size="24" color="white" />
                    </template>
                </DashboardKpiCard>
                <DashboardKpiCard
                    :label="$t('dashboard.kpis.completedEntries')"
                    :value="stats.completedEntries"
                    :variation="stats.entriesVariation"
                >
                    <template #icon>
                        <ChartColumnBig :size="24" color="white" />
                    </template>
                </DashboardKpiCard>
                <DashboardKpiCard :label="$t('dashboard.kpis.noShows')" :value="stats.noShows" :variation="null">
                    <template #icon>
                        <UserRoundX :size="24" color="white" />
                    </template>
                </DashboardKpiCard>
                <DashboardKpiCard :label="$t('dashboard.kpis.cancellations')" :value="stats.cancellations" :variation="null">
                    <template #icon>
                        <CalendarX2 :size="24" color="white" />
                    </template>
                </DashboardKpiCard>
            </div>

            <!-- Main Grid -->
            <div class="main-grid">
                <!-- Left Column - Tables -->
                <div class="tables-column">
                    <ReservationTable
                        :title="arrivingTitle"
                        :reservations="todayReservations"
                        :showStatus="true"
                        :showNote="true"
                        :viewButtonText="$t('dashboard.tables.viewCalendar')"
                        @view-calendar="goToCalendar"
                        @load-more="loadMoreReservations"
                    />

                    <ReservationTable
                        :title="$t('dashboard.tables.orderedToday')"
                        :reservations="todayOrders"
                        :showStatus="true"
                        :showNote="true"
                        :viewButtonText="$t('dashboard.tables.viewAll')"
                        @view-calendar="goToOrders"
                        @load-more="loadMoreOrders"
                    />
                </div>

                <!-- Right Column - Panels -->
                <div class="panels-column">
                    <ReservationTrends :trendsData="trendsData" :weatherData="weatherData" />
                </div>
            </div>
        </div>

        <!-- Loading overlay -->
        <div v-if="loading" class="loading-overlay">
            <v-progress-circular indeterminate color="purple" size="64"></v-progress-circular>
        </div>
    </div>
</template>

<script>
import DashboardKpiCard from './components/DashboardKpiCard.vue'
// import DateQuickFilter from './components/DateQuickFilter.vue'
import ReservationTable from './components/ReservationTable.vue'
import ReservationTrends from './components/ReservationTrends.vue'
// import AverageDelays from './components/AverageDelays.vue'
import dashboardService from '../../../services/dashboardService'
import { UserRoundX, CalendarX2, ChartColumnBig, CalendarDays } from 'lucide-vue'

export default {
    name: 'DashboardV3',
    components: {
        DashboardKpiCard,
        // DateQuickFilter,
        ReservationTable,
        ReservationTrends,
        // AverageDelays,
        UserRoundX,
        CalendarX2,
        ChartColumnBig,
        CalendarDays,
    },
    props: {
        initialUserName: {
            type: String,
            default: 'Benjamin',
        },
        city: {
            type: String,
            default: 'Paris',
        },
    },
    computed: {
        capitalizedUserName() {
            if (!this.userName) return ''
            return this.userName.charAt(0).toUpperCase() + this.userName.slice(1)
        },
        timeOfDayGreeting() {
            const hour = new Date().getHours()
            if (hour >= 5 && hour < 12) return 'morning'
            if (hour >= 18 || hour < 5) return 'evening'
            return 'hello'
        },
        arrivingTitle() {
            if (this.selectedPeriod === 'today') {
                return this.$t('dashboard.tables.arrivingToday')
            }
            // For other periods, construct "Arriving [period]"
            // If the period translation is available, use it
            const periodLabel = this.$t('dashboard.periods.' + this.selectedPeriod)
            if (periodLabel && !periodLabel.includes('dashboard.periods')) {
                return this.$t('dashboard.tables.arriving') + ' ' + periodLabel.toLowerCase()
            }
            return this.$t('dashboard.tables.arrivingToday')
        },
    },
    data() {
        return {
            loading: false,
            selectedPeriod: 'today',
            userName: this.initialUserName,

            // Stats
            stats: {
                upcomingReservations: 0,
                upcomingVariation: null,
                completedEntries: 0,
                entriesVariation: null,
                noShows: 0,
                noShowsVariation: null,
                cancellations: 0,
                cancellationsVariation: null,
            },

            // Weather
            weatherData: null,
            currentWeather: null,

            // Reservations
            todayReservations: [],
            todayOrders: [],

            // Pagination state
            reservationsPagination: {
                currentPage: 1,
                lastPage: 1,
                hasMore: false,
            },
            ordersPagination: {
                currentPage: 1,
                lastPage: 1,
                hasMore: false,
            },

            // Trends data from API
            trendsData: [],

            // Delay formulas (static for now, would need API)
            delayFormulas: [
                { name: 'Formule Anniversaire', days: 23.4, variation: 12 },
                { name: 'Formule Premium', days: 18.5, variation: 8 },
                { name: 'Formule Standard', days: 15.2, variation: -3 },
                { name: 'Formule Groupe', days: 28.7, variation: 15 },
                { name: 'Formule Entreprise', days: 35.1, variation: 5 },
            ],
        }
    },
    async mounted() {
        await this.loadAllData()
    },
    methods: {
        async loadAllData() {
            this.loading = true

            try {
                // Load data in parallel
                await Promise.all([
                    this.loadStats(),
                    this.loadReservations(),
                    this.loadOrders(),
                    this.loadWeather(),
                    this.loadReservationTrends(),
                ])
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.loading = false
            }
        },

        async loadStats() {
            try {
                const response = await dashboardService.getStats(this.selectedPeriod)
                if (response) {
                    this.stats = {
                        upcomingReservations: response.upcoming_soon || 0,
                        upcomingVariation: this.calculateVariation(response.upcoming_soon, response.previous_upcoming_soon),
                        completedEntries: response.passed_reservations || 0,
                        entriesVariation: null, // this.calculateVariation(
                        //     response.passed_reservations,
                        //     response.passed_reservations
                        // ),
                        noShows: response.no_show_count || 0,
                        noShowsVariation: -3, // Placeholder
                        cancellations: response.total_reservation_canceled || 0,
                        cancellationsVariation: -3, // Placeholder
                    }
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error loading stats:', error)
            }
        },

        async loadReservations() {
            try {
                const response = await dashboardService.getReservations(this.selectedPeriod, 1, 15)
                if (response) {
                    this.todayReservations = response.today_reservations_list || []

                    if (response.pagination) {
                        this.reservationsPagination = {
                            currentPage: response.pagination.current_page,
                            lastPage: response.pagination.last_page,
                            hasMore: response.pagination.current_page < response.pagination.last_page,
                        }
                    }
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error loading reservations:', error)
            }
        },

        async loadOrders() {
            try {
                const response = await dashboardService.getOrders(this.selectedPeriod, 1, 15)
                if (response && response.purchase_list) {
                    // Convert orders to reservation-like format for the table
                    this.todayOrders = response.purchase_list.map((order) => ({
                        id: order.id,
                        booking: {
                            time_slots: [
                                {
                                    start: new Date(order.created_at).getTime() / 1000,
                                },
                            ],
                        },
                        items: [
                            {
                                itemable: { name: this.$t('dashboard.tables.order_id', { id: order.id }) },
                                nb_contributor: order.items?.length || 1,
                            },
                        ],
                        user: order.user,
                        total_price: order.total_price,
                        current_price: order.current_price,
                        status: order.status,
                        note: order.comment,
                    }))

                    if (response.pagination) {
                        this.ordersPagination = {
                            currentPage: response.pagination.current_page,
                            lastPage: response.pagination.last_page,
                            hasMore: response.pagination.current_page < response.pagination.last_page,
                        }
                    }
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error loading orders:', error)
            }
        },

        async loadMoreReservations() {
            if (!this.reservationsPagination.hasMore || this.loading) return

            console.log('Loading more reservations, current page:', this.reservationsPagination.currentPage)

            try {
                const nextPage = this.reservationsPagination.currentPage + 1
                const response = await dashboardService.getReservations(this.selectedPeriod, nextPage, 15)

                console.log('Load more reservations response:', response)

                if (response) {
                    const newReservations = response.today_reservations_list || []
                    console.log('New reservations to append:', newReservations.length, newReservations)
                    console.log('Current reservations count:', this.todayReservations.length)

                    // Create completely new array to force Vue reactivity
                    const combined = this.todayReservations.concat(newReservations)
                    this.$set(this, 'todayReservations', combined)

                    console.log('Total reservations after append:', this.todayReservations.length)
                    console.log('Updated array:', this.todayReservations)

                    if (response.pagination) {
                        this.reservationsPagination = {
                            currentPage: response.pagination.current_page,
                            lastPage: response.pagination.last_page,
                            hasMore: response.pagination.current_page < response.pagination.last_page,
                        }
                        console.log('Updated pagination state:', this.reservationsPagination)
                    }
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error loading more reservations:', error)
            }
        },

        async loadMoreOrders() {
            if (!this.ordersPagination.hasMore || this.loading) return

            console.log('Loading more orders, current page:', this.ordersPagination.currentPage)

            try {
                const nextPage = this.ordersPagination.currentPage + 1
                const response = await dashboardService.getOrders(this.selectedPeriod, nextPage, 15)

                console.log('Load more orders response:', response)

                if (response && response.purchase_list) {
                    console.log('New orders to append:', response.purchase_list.length, response.purchase_list)
                    console.log('Current orders count:', this.todayOrders.length)

                    const newOrders = response.purchase_list.map((order) => ({
                        id: order.id,
                        booking: {
                            time_slots: [
                                {
                                    start: new Date(order.created_at).getTime() / 1000,
                                },
                            ],
                        },
                        items: [
                            {
                                itemable: { name: this.$t('dashboard.tables.order_id', { id: order.id }) },
                                nb_contributor: order.items?.length || 1,
                            },
                        ],
                        user: order.user,
                        total_price: order.total_price,
                        current_price: order.current_price,
                        status: order.status,
                        note: order.comment,
                    }))

                    // Create completely new array to force Vue reactivity
                    const combined = this.todayOrders.concat(newOrders)
                    this.$set(this, 'todayOrders', combined)

                    console.log('Total orders after append:', this.todayOrders.length)
                    console.log('Updated array:', this.todayOrders)

                    if (response.pagination) {
                        this.ordersPagination = {
                            currentPage: response.pagination.current_page,
                            lastPage: response.pagination.last_page,
                            hasMore: response.pagination.current_page < response.pagination.last_page,
                        }
                        console.log('Updated pagination state:', this.ordersPagination)
                    }
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error loading more orders:', error)
            }
        },
        async loadWeather() {
            console.log('DashboardV3: Loading weather for city:', this.city)
            console.log('DashboardV3: API Key available:', !!window.weatherApiKey)
            try {
                const lang = this.$i18n.locale || 'fr'
                this.weatherData = await dashboardService.getWeather(this.city, null, lang)

                // Get current weather
                const currentResponse = await dashboardService.getCurrentWeather(this.city, null, lang)
                console.log('DashboardV3: Current weather response:', currentResponse)
                if (currentResponse && currentResponse.main) {
                    this.currentWeather = {
                        temp: currentResponse.main.temp,
                        icon: currentResponse.weather?.[0]?.icon,
                        description: currentResponse.weather?.[0]?.description,
                    }
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error loading weather:', error)
            }
        },

        async loadReservationTrends() {
            try {
                const response = await dashboardService.getReservationTrends()
                if (response && response.success && response.data) {
                    this.trendsData = response.data
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error loading reservation trends:', error)
            }
        },

        calculateVariation(current, previous) {
            if (!previous || previous === 0) return null
            return Math.round(((current - previous) / previous) * 100)
        },

        async onPeriodChange(period) {
            this.selectedPeriod = period
            if (period !== 'custom') {
                await this.loadAllData()
            }
            // TODO: Handle custom date picker
        },

        goToCalendar() {
            window.open('/back-office/planning', '_blank')
        },

        goToOrders() {
            window.open('/back-office/invoices-en-attente', '_blank')
        },

        goToStatistics() {
            window.location.href = '/back-office/statistiques'
        },
    },
}
</script>

<style scoped>
.dashboard-v3 {
    position: relative;
    padding: 20px;
    overflow-x: hidden;
}

.dashboard-background {
    position: fixed;
    top: 0;
    left: 260px;
    right: 0;
    bottom: 0;
    background: linear-gradient(159deg, #0f123b 14.25%, #090d2e 56.45%, #020515 86.14%);
    z-index: -1;
}

.dashboard-content {
    position: relative;
    z-index: 1;
    max-width: 1800px;
    margin: 0 auto;
}

.dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
}

.header-actions {
    display: flex;
    gap: 20px;
    align-items: center;
}

.view-toggles {
    display: flex;
    border: 1px solid #fff;
    border-radius: 30px;
    padding: 1.5px;
}

.toggle {
    background: none;
    border: none;
    padding: 4px 12px;
    border-radius: 25px;
    font-size: 12px;
    cursor: pointer;
    font-weight: 500;
    color: #fff;
    transition: all 0.2s;
}

.toggle:hover {
    background: rgba(30, 27, 57, 0.1);
}

.toggle.active {
    color: #1e1b39;
    background-color: #fff;
}

.greeting-text {
    color: #fff;
    font-family: 'Poppins', sans-serif;
    font-size: 27px;
    font-weight: 400;
    margin: 0;
}

.user-name {
    font-weight: 700;
}

.kpi-row {
    display: flex;
    gap: 20px;
    margin-bottom: 24px;
    flex-wrap: wrap;
}

.kpi-row > * {
    flex: 1;
    min-width: 280px;
}

.main-grid {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 24px;
}

.tables-column {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.panels-column {
    display: flex;
    flex-direction: column;
    gap: 30px;
}

.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

/* Responsive */
@media (max-width: 1400px) {
    .main-grid {
        grid-template-columns: 1fr;
    }

    .panels-column {
        flex-direction: row;
    }

    .panels-column > * {
        flex: 1;
    }
}

@media (max-width: 900px) {
    .dashboard-header {
        flex-direction: column;
        gap: 20px;
        align-items: flex-start;
    }

    .kpi-row {
        flex-direction: column;
    }

    .kpi-row > * {
        min-width: 100%;
    }

    .panels-column {
        flex-direction: column;
    }
}
@media (max-width: 768px) {
    .tables-column {
        min-width: 90vw;
    }
}
</style>
